import * as trace from 'owa-trace';
import { getBusinessUserOCPSPolicies } from '../services/getBusinessUserOCPSPolicies';
import { getSuccessfulResponsePolicies } from './getSuccessfulResponsePolicies';
import { OCPSDataStorePolicyValue } from '../store/schema/OCPSDataStoreType';
import {
    doesPolicyExist,
    havePreviousPoliciesExpired,
    resetOCPSDataStore,
    setHasError,
    setOCPSErrorMessage,
    setPolicy,
    setPolicyExpirationDate,
} from './ocpsStoreUtils';

import {
    FEEDBACK_SETTING_ID,
    EMAIL_COLLECTION_SETTING_ID,
    CHECK_IN_INTERVAL_HEADER_KEY,
    USER_OCPS_FEEDBACK_ENABLED,
    USER_OCPS_EMAIL_COLLECTION_ENABLED,
    DEFAULT_CHECK_IN_INTERVAL,
} from './constants';

/**
 * Maps the value in network response for a policy to an enum in store
 * @param policyValue
 */
const getOCPSDataStorePolicyValueFromResponsePolicyValue = (policyValue: string) =>
    policyValue === '1' ? OCPSDataStorePolicyValue.Enabled : OCPSDataStorePolicyValue.Disabled;

/**
 * Fetches policies if policies aren't present in store or they've expired.
 * Stores the new set of fetched policies to store.
 */
export const fetchPoliciesAndSetStore = async () => {
    if (
        !havePreviousPoliciesExpired() &&
        [USER_OCPS_EMAIL_COLLECTION_ENABLED, USER_OCPS_FEEDBACK_ENABLED].every(doesPolicyExist)
    ) {
        /*
         * Return if you already have the policies AND the policies haven't expired.
         */
        return;
    }

    /**
     * If we have to fetch the policies, begin by resetting the store as the policies
     * may have changed after the latest iteration and we might end up using stale policies
     * if the new response does not contain the policy then also the store might have it from before.
     */
    resetOCPSDataStore();

    try {
        const response = await getBusinessUserOCPSPolicies();
        const ocpsResponse = await getSuccessfulResponsePolicies(response);
        const checkInInterval = response.headers.has(CHECK_IN_INTERVAL_HEADER_KEY)
            ? response.headers.get(CHECK_IN_INTERVAL_HEADER_KEY)
            : DEFAULT_CHECK_IN_INTERVAL; // default value;

        setPolicyExpirationDate(checkInInterval);

        ocpsResponse?.value?.[0]?.policiesPayload?.forEach(policy => {
            switch (policy.settingId) {
                case FEEDBACK_SETTING_ID:
                    setPolicy(
                        USER_OCPS_FEEDBACK_ENABLED,
                        getOCPSDataStorePolicyValueFromResponsePolicyValue(policy.value)
                    );
                    break;

                case EMAIL_COLLECTION_SETTING_ID:
                    setPolicy(
                        USER_OCPS_EMAIL_COLLECTION_ENABLED,
                        getOCPSDataStorePolicyValueFromResponsePolicyValue(policy.value)
                    );
                    break;
            }
        });

        /**
         * If the policies are not configured, set them to default values present at
         * https://www.owiki.ms/wiki/Working_with_feedback_policies
         */
        [
            {
                policyName: USER_OCPS_FEEDBACK_ENABLED,
                policyDefaultValue: OCPSDataStorePolicyValue.Enabled,
            },
            {
                policyName: USER_OCPS_EMAIL_COLLECTION_ENABLED,
                policyDefaultValue: OCPSDataStorePolicyValue.Disabled,
            },
        ].forEach(
            ({ policyName, policyDefaultValue }) =>
                !doesPolicyExist(policyName) && setPolicy(policyName, policyDefaultValue)
        );
    } catch (error) {
        /**
         * If error happened while fetching the policies reset the store so that we aren't left with any
         * stale data from previous response
         */
        trace.errorThatWillCauseAlert(
            `fetchPoliciesAndSetStore: Failed to fetch/parse policies: ${error.message}`
        );
        resetOCPSDataStore();
        setHasError(true);
        setOCPSErrorMessage(error.message);
    }
};
